import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  CardActions,
  CardContent,
  Checkbox,
  CircularProgress,
  Grid,
  Typography,
} from "@mui/material";

import { unwrapResult } from "@reduxjs/toolkit";
import BackdropPrimaryMain from "components/BackdropPrimaryMain";
import ConfirmationFab from "components/ConfirmationFab";
import StyledCard from "components/StyledCard";
import { StyledContainer } from "components/StyledContainer";
import { GraphQLError } from "graphql";
import { isAbortError } from "helpers";
import { TechnicianAssignment } from "models/TechnicianAssignment";
import { useAppDispatch, useAppSelector } from "store";
import { selectPageSelectedJob } from "store/root.store";
import { assignTask } from "store/slices/planner.store";
import { addSnackbarMessage } from "store/slices/snackbar.store";

export const TechnicianAssign = () => {
  const dispatch = useAppDispatch();
  const job = useAppSelector(selectPageSelectedJob);
  const { technicians } = useAppSelector((s) => s.cache);
  const { loadingAssignTask } = useAppSelector((s) => s.planner);
  const navigate = useNavigate();
  const [options, setOptions] = useState<TechnicianAssignment[]>([]);

  useEffect(() => {
    if (job) {
      setOptions(
        technicians.map((t) => ({
          technician: t,
          checked: job.assignedResources.findIndex((x) => x.id === t.id) > -1,
        })),
      );
    }
  }, [job, job.assignedResources, technicians]);

  const assignTaskCb = useCallback(() => {
    dispatch(
      assignTask({
        jobId: job.id,
        technicianIds: options.filter((x) => x.checked).map((x) => x.technician.id),
      }),
    )
      .then(unwrapResult)
      .then(() => {
        dispatch(addSnackbarMessage({ key: "AssignTask-success" }));
        navigate("/planner", { state: { openFilter: false } });
      })
      .catch((error: GraphQLError[] | Error) => {
        if (isAbortError(error)) return;
        if (Array.isArray(error) && error.some((e) => e.message.includes("403 Forbidden"))) {
          dispatch(addSnackbarMessage({ key: "AssignTask-forbidden" }));
        } else {
          dispatch(addSnackbarMessage({ key: "AssignTask-fail" }));
        }
      });
  }, [dispatch, job.id, navigate, options]);

  const checkResource = useCallback(
    (option: TechnicianAssignment, index: number) => {
      setOptions([
        ...options.slice(0, index),
        { technician: option.technician, checked: !option.checked },
        ...options.slice(index + 1),
      ]);
    },
    [options],
  );

  return (
    <StyledContainer data-testid="TechnicianAssignDialog">
      <BackdropPrimaryMain open={loadingAssignTask}>
        <CircularProgress color="inherit" />
      </BackdropPrimaryMain>
      <Grid container minWidth="100%" alignItems="center" sx={{ marginBottom: "75px" }}>
        {options.slice().map((x, index) => (
          <Grid item xs={12} key={x.technician.id}>
            <StyledCard onClick={() => checkResource(x, index)}>
              <CardContent>
                <Typography variant="body1" component="h3">
                  {x.technician.name}
                </Typography>
              </CardContent>
              <CardActions>
                <Checkbox
                  color="primary"
                  checked={x.checked}
                  onChange={() => checkResource(x, index)}
                />
              </CardActions>
            </StyledCard>
          </Grid>
        ))}
      </Grid>
      <ConfirmationFab disabled={options.every((x) => !x.checked)} onClick={assignTaskCb} />
    </StyledContainer>
  );
};
